import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';

import firebaseApp from '@/config/firebase.config';
import { Constants } from '@/constants';

export type PermissionAction = 'read' | 'write';

export interface RolePermission {
  permission_group_id: number;
  key: string;
  name: string;
  menuSection?: string | null;
  description?: string | null;
  can_read: boolean;
  can_write: boolean;
}

export interface PermissionGroup {
  permission_group_id: number;
  key: string;
  name: string;
  menu_section?: string | null;
  description?: string | null;
}

export interface AdminRoleSummary {
  role_id: number;
  key: string;
  name: string;
  description?: string | null;
  is_system: boolean;
}

export interface AdminRole extends AdminRoleSummary {
  permissions: RolePermission[];
}

export interface AdminAccess {
  uid: string;
  email: string | null;
  roleIds: number[];
  roles: AdminRoleSummary[];
  isOwner: boolean;
  isSuperuser: boolean;
  permissions: string[];
}

export interface AdminTeamUser extends AdminAccess {
  displayName: string | null;
  photoURL: string | null;
  disabled: boolean;
  emailVerified: boolean;
  createdAt: string | null;
  lastSignInAt: string | null;
}

export interface TeamListResponse {
  users: AdminTeamUser[];
  nextPageToken: string | null;
}

export interface TeamUserInput {
  email?: string;
  displayName?: string;
  password?: string;
  disabled?: boolean;
  roleIds?: number[];
}

export interface CreateTeamUserResponse {
  user: AdminTeamUser;
  passwordResetLink: string | null;
}

const waitForFirebaseUser = () =>
  new Promise<firebase.User>((resolve, reject) => {
    const { currentUser } = firebaseApp.auth();
    if (currentUser) {
      resolve(currentUser);
      return;
    }

    const unsubscribe = firebaseApp.auth().onAuthStateChanged((user) => {
      unsubscribe();
      if (user) resolve(user);
      else reject(new Error('Admin authentication is required'));
    }, reject);
  });

export const getAdminIdToken = async (forceRefresh = false) => {
  const user = await waitForFirebaseUser();
  return user.getIdToken(forceRefresh);
};

const adminRequest = async <T>(config: AxiosRequestConfig) => {
  const token = await getAdminIdToken();
  return axios.request<T>({
    ...config,
    baseURL: Constants.apiPath,
    headers: {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    },
  });
};

export const refreshCurrentAdminToken = async () => {
  return getAdminIdToken(true);
};

export const getCurrentAdminAccess = async () =>
  (await adminRequest<AdminAccess>({ method: 'GET', url: '/access/me' })).data;

export const hasPermission = (
  access: AdminAccess | null | undefined,
  key: string,
  action: PermissionAction = 'read'
) =>
  Boolean(
    access?.isSuperuser ||
      access?.permissions.includes('*') ||
      access?.permissions.includes(`${key}:${action}`)
  );

export const hasAnyPermission = (
  access: AdminAccess | null | undefined,
  keys: string[],
  action: PermissionAction = 'read'
) => keys.some((key) => hasPermission(access, key, action));

export const hasAdminRole = (access: AdminAccess | null | undefined) =>
  Boolean(
    access?.isOwner ||
      access?.isSuperuser ||
      access?.roles.some((role) => role.key.toLowerCase() === 'admin')
  );

export const listPermissionGroups = async () =>
  (
    await adminRequest<PermissionGroup[]>({
      method: 'GET',
      url: '/permissions',
    })
  ).data;

export const listRoles = async () =>
  (await adminRequest<AdminRole[]>({ method: 'GET', url: '/roles' })).data;

export const getRole = async (roleId: number) =>
  (
    await adminRequest<AdminRole>({
      method: 'GET',
      url: `/roles/${roleId}`,
    })
  ).data;

export const createRole = async (role: {
  key: string;
  name: string;
  description?: string;
  permissions?: RolePermission[];
}) =>
  (await adminRequest<AdminRole>({ method: 'POST', url: '/roles', data: role }))
    .data;

export const updateRole = async (roleId: number, role: Partial<AdminRole>) =>
  (
    await adminRequest<AdminRole>({
      method: 'PUT',
      url: `/roles/${roleId}`,
      data: role,
    })
  ).data;

export const deleteRole = async (roleId: number) =>
  adminRequest<void>({ method: 'DELETE', url: `/roles/${roleId}` });

export const listTeamUsers = async (params?: {
  q?: string;
  limit?: number;
  pageToken?: string;
}) =>
  (
    await adminRequest<TeamListResponse>({
      method: 'GET',
      url: '/team',
      params,
    })
  ).data;

export const createTeamUser = async (user: TeamUserInput) =>
  (
    await adminRequest<CreateTeamUserResponse>({
      method: 'POST',
      url: '/team',
      data: user,
    })
  ).data;

export const updateTeamUser = async (uid: string, user: TeamUserInput) =>
  (
    await adminRequest<AdminTeamUser>({
      method: 'PATCH',
      url: `/team/${uid}`,
      data: user,
    })
  ).data;

export const generateTeamPasswordResetLink = async (uid: string) =>
  (
    await adminRequest<{ passwordResetLink: string }>({
      method: 'POST',
      url: `/team/${uid}/password-reset-link`,
    })
  ).data.passwordResetLink;

export const getApiErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const responseMessage = error.response?.data?.message;
    if (typeof responseMessage === 'string') return responseMessage;
    if (typeof error.response?.data === 'string') return error.response.data;
    return error.message;
  }
  return error instanceof Error ? error.message : 'Something went wrong';
};
